import { SectionIds } from '@bupa-kkv/features/company-creation/types';
import { queryKeyStore } from '@bupa-monorepo/shared-frontend/query-key-store';
import {
  getElementFromGroup,
  getPageConfigurationElement,
  getPageConfigurationGroup,
  isDefined,
} from '@bupa-monorepo/shared-frontend/utils';
import {
  IConfigurationGroup,
  IPageConfiguration,
} from '@bupa-monorepo/shared-types/page-configuration-types';
import { useQuery } from '@tanstack/react-query';
import { fetchConfiguration } from '../api/configuration.api';
import {
  CardConfiguration,
  DashboardPageConfiguration,
} from '../types/configuration.types';

export const useConfiguration = () => {
  return useQuery({
    queryKey: [queryKeyStore.companyCreation.dashboardConfig.queryKey],
    queryFn: () => fetchConfiguration('dashboard'),
    select: mapResponse,
  });
};

const mapResponse = (
  pageConfiguration: IPageConfiguration
): DashboardPageConfiguration => {
  return {
    selfEmpCardConfig: mapCardConfig({
      pageConfiguration,
      sectionId: SectionIds.COMPANY_CREATION_BLOCKS,
      groupId: 'tile1',
      bulletpointGroupId: 'EvStep',
    }),
    companyCardConfig: mapCardConfig({
      pageConfiguration,
      sectionId: SectionIds.COMPANY_CREATION_BLOCKS,
      groupId: 'tile2',
      bulletpointGroupId: 'SpStep',
    }),
  };
};

const mapCardConfig = ({
  pageConfiguration,
  sectionId,
  groupId,
  bulletpointGroupId,
}: {
  pageConfiguration: IPageConfiguration;
  sectionId: string;
  groupId: string;
  bulletpointGroupId: string;
}): CardConfiguration => ({
  title:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: groupId,
      elementId: 'title',
    })?.value ?? '',
  description:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: groupId,
      elementId: 'description',
    })?.value ?? '',
  moreInfoLink:
    getPageConfigurationElement({
      pageConfiguration,
      sectionId,
      groupId: groupId,
      elementId: 'link',
    })?.value ?? '',
  bulletpoints: mapSectionInstructionSteps({
    pageConfiguration,
    sectionId,
    groupId: bulletpointGroupId,
  }),
});

const mapSectionInstructionSteps = ({
  pageConfiguration,
  sectionId,
  groupId,
}: {
  pageConfiguration: IPageConfiguration;
  sectionId: string;
  groupId: string;
}): string[] => {
  return [
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}1`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}2`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}3`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}4`,
    }),
    getPageConfigurationGroup({
      pageConfiguration,
      sectionId,
      groupId: `${groupId}5`,
    }),
  ]
    .filter(isDefined)
    .filter((group) => group?.isVisible)
    .map(mapInstructionStep);
};

const mapInstructionStep = (group: IConfigurationGroup) => {
  return getElementFromGroup({ group, elementId: 'description' })?.value ?? '';
};
